import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { AstroFile } from '@/models/AstroFile';
import alert from '@/utils/shared/alert';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileCard from './FileCard';
import FileInputZone from './FileInputZone';
import { uniqueId } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';

type MultiFileUploaderProps = {
  value?: AstroFile[];
  label?: string;
  limit?: number;
  accept?: string;
  testId?: string;
  maxFiles?: number;
  onChange: (files: AstroFile[]) => void;
};

const MultiFileUploader = ({
  value,
  limit,
  onChange,
  accept,
  label,
  maxFiles = Infinity,
  testId,
}: MultiFileUploaderProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<AstroFile[]>([]);

  const createAstroFile = (file: File): AstroFile => {
    return {
      file,
      fileName: file.name,
      fileKey: uniqueId('local-'),
      createdAt: new Date().toISOString(),
    };
  };

  const handleFileUpload = (uploadedFiles: FileList) => {
    if (!uploadedFiles.length) return;
    const newAstroFiles = Array.from(uploadedFiles).map(file =>
      createAstroFile(file),
    );

    if (maxFiles && files.length + newAstroFiles.length > maxFiles) {
      alert.warning(t('common.maxFilesLimitReached', { count: maxFiles }));
      return;
    }

    const updatedFiles = [...files, ...newAstroFiles];

    setFiles(updatedFiles);
    onChange(updatedFiles);
  };

  const onRemoveFile = (index: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onChange(updatedFiles);
  };

  const getUrlForFile = (astroFile: AstroFile): string | undefined => {
    if (
      astroFile.fileKey.startsWith('local-') &&
      astroFile.file instanceof File
    ) {
      return URL.createObjectURL(astroFile.file);
    }

    if (
      astroFile.fileKey.startsWith('server-') &&
      typeof astroFile.file === 'string'
    ) {
      return astroFile.file;
    }

    return undefined;
  };

  useEffect(() => {
    if (!value || value.length === 0) {
      setFiles([]);
      return;
    }

    setFiles(value);
  }, [value]);

  const canUploadMore = files.length < maxFiles;

  return (
    <div className="flex gap-2 w-full">
      <ConditionalRenderer condition={canUploadMore}>
        <div className="relative h-36 aspect-video">
          <FileInputZone
            onChange={handleFileUpload}
            label={label}
            testId={testId}
            multiple
            accept={accept}
            limit={limit}
            className={{
              base: 'w-full h-full bg-base-100',
              section: 'min-h-16 p-3',
            }}
          />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!canUploadMore}>
        <div className="py-2 px-4 bg-warning-content rounded-lg border border-warning border-dashed relative h-36 aspect-video flex items-center justify-center">
          <Text
            text={t('common.maxFilesLimitReached', {
              count: maxFiles,
            })}
            className="text-sm text-warning"
          />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={files.length > 0}>
        <div
          className={twMerge(
            'flex flex-wrap gap-2 w-full max-h-44',
            scrollBarYClassName,
          )}
        >
          {files.map((file, index) => (
            <FileCard
              key={file.fileKey}
              file={file}
              onRemove={onRemoveFile(index)}
              url={getUrlForFile(file)}
            />
          ))}
        </div>
      </ConditionalRenderer>
    </div>
  );
};

export default MultiFileUploader;
